import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

/* ────────────────────────────────────────────────────────────────
   "What's New" pop-up.
   Shows ONCE per release to every logged-in user, then never again
   until you ship the next update.

   To announce a new update later:
     1. Bump RELEASE to a new string (e.g. 'aug2026-1').
     2. Replace TITLE / FEATURES below.
   Everyone sees the pop-up one time on their next visit.
   ──────────────────────────────────────────────────────────────── */
const RELEASE = 'jul2026-1';
const SEEN_KEY = 'ryzenos_whatsnew';

const TITLE = "What's new in RyzenOS";
const SUBTITLE = 'A big update just landed. Here’s what your team gets:';

const FEATURES = [
  { t: 'Multiple businesses, one account', d: 'Run a separate pipeline and stages for each business — switch from a dropdown.' },
  { t: 'Custom pipeline stages', d: 'Rename stages and add your own columns to match how your team works.' },
  { t: 'Won leads become projects — automatically', d: 'Mark a lead Won and RyzenOS creates the client and project for you.' },
  { t: 'Daily reports + branded PDFs', d: 'An evening summary in your inbox, plus downloadable daily/weekly/monthly reports.' },
  { t: 'Smarter Finance', d: 'Revenue, expenses and profit at a glance, with a payment-status column.' },
  { t: 'Cleaner invoices', d: 'Sequential invoice numbers, auto-saved to your Files.' },
  { t: 'Built for mobile', d: 'Your sales team gets instant notifications the moment a lead is theirs.' },
];

export default function WhatsNew() {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const dark = theme !== 'light';

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    let seen = null;
    try { seen = localStorage.getItem(SEEN_KEY); } catch (e) {}
    if (seen !== RELEASE) setOpen(true);
  }, [isAuthenticated, loading]);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, RELEASE); } catch (e) {}
    setOpen(false);
  };

  if (!open) return null;

  const bg      = dark ? '#17171b' : '#ffffff';
  const border  = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const text    = dark ? '#ffffff' : '#0f0c08';
  const muted   = dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
  const rowBg   = dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';

  return (
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px', fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: bg, color: text, border: `1px solid ${border}`,
          borderRadius: 18, width: '100%', maxWidth: 440,
          maxHeight: '86vh', overflowY: 'auto',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          padding: '22px 22px 18px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{
              display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: '#F5A623',
              background: 'rgba(245,166,35,0.12)', padding: '4px 9px', borderRadius: 20, marginBottom: 10,
            }}>New update</div>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{TITLE}</div>
            <div style={{ fontSize: 13, color: muted, marginTop: 4 }}>{SUBTITLE}</div>
          </div>
          <div
            onClick={dismiss}
            style={{
              cursor: 'pointer', color: muted, fontSize: 22, lineHeight: 1,
              padding: '2px 6px', flexShrink: 0, userSelect: 'none',
            }}
          >×</div>
        </div>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{
              display: 'flex', gap: 11, alignItems: 'flex-start',
              background: rowBg, border: `1px solid ${border}`,
              borderRadius: 12, padding: '11px 13px',
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: 4, background: '#F5A623',
                marginTop: 6, flexShrink: 0,
              }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{f.t}</div>
                <div style={{ fontSize: 12.5, color: muted, marginTop: 2, lineHeight: 1.45 }}>{f.d}</div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={dismiss}
          style={{
            marginTop: 18, width: '100%', border: 'none', cursor: 'pointer',
            background: '#F5A623', color: '#17120a', fontWeight: 800, fontSize: 15,
            padding: '13px', borderRadius: 12, fontFamily: 'inherit',
          }}
        >Got it</button>
      </div>
    </div>
  );
}
